import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field, Select, Btn, MpinField, ReceiptDialog, DataTable, FilterBar, StatusBadge } from "@/components/shared";
import { useMeta, useSubmit } from "@/pages/Services";
import { chargeFor } from "@/pages/Charges";
import BulkPayout from "@/pages/BulkPayout";

export function FundRequest() {
  const meta = useMeta();
  const [banks, setBanks] = useState([]);
  useEffect(() => { api.get("/company-banks").then(({ data }) => setBanks(data)).catch(() => {}); }, []);
  const blank = { bank_id: "", amount: "", utr: "", payment_mode: "", deposit_date: today(), remarks: "", slip: "" };
  const [f, setF] = useState(blank);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2e6) return toast.error("Max 2MB file");
    const r = new FileReader();
    r.onload = () => setF((p) => ({ ...p, slip: r.result }));
    r.readAsDataURL(file);
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/fund-requests", { ...f, amount: Number(f.amount) });
      toast.success(`Fund request ${data.req_id} submitted`);
      setF(blank);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title="Fund Request" subtitle="Deposit money in a company account, then submit the request. Admin approves and wallet is credited." />
      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <Card title="Company bank accounts">
          <div className="space-y-3" data-testid="company-banks">
            {banks.map((b) => (
              <div key={b.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="font-semibold">{b.bank}</div>
                <div className="text-xs text-slate-500">{b.account_name} · {b.branch}</div>
                <div className="mt-1 flex items-center gap-2 font-mono">A/c {b.account_no}
                  <button onClick={() => { navigator.clipboard?.writeText(b.account_no); toast.success("Copied"); }} data-testid={`copy-acc-${b.id}`}><Copy className="h-3.5 w-3.5 text-slate-400" /></button>
                </div>
                <div className="font-mono text-xs">IFSC {b.ifsc}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Request details">
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="fund-request-form">
            <Field label="Deposited In"><Select options={banks.map((b) => ({ value: b.id, label: `${b.bank} - ${b.account_no}` }))} placeholder="Select Bank" value={f.bank_id} onChange={set("bank_id")} required data-testid="fr-bank-select" /></Field>
            <Field label="Payment Mode"><Select options={meta?.payment_modes || []} placeholder="Select Mode" value={f.payment_mode} onChange={set("payment_mode")} required data-testid="fr-mode-select" /></Field>
            <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="fr-amount-input" /></Field>
            <Field label="UTR / Reference No."><input className="np-input font-mono" value={f.utr} onChange={set("utr")} required data-testid="fr-utr-input" /></Field>
            <Field label="Deposit Date"><input type="date" className="np-input" value={f.deposit_date} onChange={set("deposit_date")} required data-testid="fr-date-input" /></Field>
            <Field label="Deposit Slip (optional)"><input type="file" accept="image/*,.pdf" className="np-input pt-2 text-xs" onChange={onFile} data-testid="fr-slip-input" /></Field>
            <Field label="Remarks" className="sm:col-span-2"><input className="np-input" value={f.remarks} onChange={set("remarks")} data-testid="fr-remarks-input" /></Field>
            <Btn type="submit" loading={loading} data-testid="fr-submit-button">Submit Request</Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

export function FundHistory() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async (f = {}) => {
    setLoading(true);
    const { data } = await api.get("/fund-requests", { params: { ...f, status: status || undefined } });
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const cancel = async (id) => {
    try { await api.post(`/fund-requests/${id}/cancel`); toast.success("Request cancelled"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approved = rows.filter((r) => r.status === "APPROVED");
  const cols = [
    { label: "Date", render: (r) => fmtDate(r.created_at), csv: (r) => r.created_at },
    { label: "Req ID", key: "req_id", render: (r) => <span className="font-mono text-xs">{r.req_id}</span> },
    { label: "Amount", key: "amount", render: (r) => <span className="font-mono font-semibold">{inr(r.amount)}</span> },
    { label: "Mode", key: "payment_mode" }, { label: "UTR", key: "utr" }, { label: "Deposit Date", key: "deposit_date" },
    { label: "Status", key: "status", render: (r) => <StatusBadge status={r.status} /> },
    { label: "Admin Remarks", key: "admin_remarks" },
    { label: "Action", render: (r) => r.status === "PENDING" ? <button className="text-xs font-semibold text-red-600" onClick={() => cancel(r.id)} data-testid={`cancel-fr-${r.req_id}`}>Cancel</button> : "-" },
  ];
  return (
    <div>
      <PageHeader title="Fund Request History" />
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Card><div className="np-label">Approved Amount</div><div className="font-mono text-xl font-semibold" data-testid="fr-approved-amount">{inr(approved.reduce((s, r) => s + r.amount, 0))}</div></Card>
        <Card><div className="np-label">Approved Requests</div><div className="text-xl font-semibold">{approved.length}</div></Card>
        <Card><div className="np-label">Rejected / Cancelled</div><div className="text-xl font-semibold">{rows.filter((r) => ["REJECTED", "CANCELLED"].includes(r.status)).length}</div></Card>
      </div>
      <FilterBar onSearch={load} loading={loading} exportRows={rows} exportName="fund-history" exportCols={cols.slice(0, 8)}>
        <Field label="Status"><Select options={["PENDING", "APPROVED", "REJECTED", "CANCELLED"]} placeholder="All" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="fr-status-filter" /></Field>
      </FilterBar>
      <DataTable columns={cols} rows={rows} testid="fund-history-table" />
    </div>
  );
}

function BankInput({ banks, value, onChange, testid }) {
  return (
    <>
      <input className="np-input" list={`${testid}-list`} placeholder="Bank name (type or pick)" value={value} onChange={onChange} required minLength={3} data-testid={testid} />
      <datalist id={`${testid}-list`}>{banks.map((b) => <option key={b} value={b} />)}</datalist>
    </>
  );
}

function SettlementAccounts({ accounts, reload, banks }) {
  const blank = { bank: "", account_no: "", ifsc: "", holder_name: "" };
  const [f, setF] = useState(blank);
  const [show, setShow] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/settlement-accounts", f); toast.success("Account added"); setF(blank); setShow(false); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/settlement-accounts/${id}`); reload(); };
  return (
    <Card title="Settlement accounts" action={<Btn variant="outline" className="h-8" onClick={() => setShow(!show)} data-testid="add-settle-account-button"><Plus className="h-4 w-4" />Add</Btn>}>
      {show && (
        <form onSubmit={add} className="mb-4 grid gap-3 rounded-lg bg-slate-50 p-3" data-testid="settle-account-form">
          <BankInput banks={banks} value={f.bank} onChange={set("bank")} testid="sa-bank-select" />
          <input className="np-input" placeholder="Account holder name" value={f.holder_name} onChange={set("holder_name")} required data-testid="sa-holder-input" />
          <input className="np-input font-mono" placeholder="Account number" value={f.account_no} onChange={set("account_no")} required data-testid="sa-account-input" />
          <input className="np-input font-mono uppercase" placeholder="IFSC" maxLength={11} value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value.toUpperCase() })} required data-testid="sa-ifsc-input" />
          <Btn type="submit" data-testid="sa-save-button">Save</Btn>
        </form>
      )}
      <div className="space-y-2">
        {accounts.length === 0 && !show && <div className="py-6 text-center text-sm text-slate-400" data-testid="no-settle-accounts">No bank account added yet. Click Add to save your bank account for payouts.</div>}
        {accounts.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm" data-testid={`settle-acc-${a.account_no}`}>
            <div><div className="font-semibold">{a.holder_name}</div><div className="text-xs text-slate-500">{a.bank} · <span className="font-mono">{a.account_no}</span> · {a.ifsc}</div></div>
            <button onClick={() => del(a.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-settle-${a.account_no}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function MoveToBank() {
  const meta = useMeta();
  const { wallet, user } = useAuth();
  const tabs = [["saved", "Saved Bank", "payout_saved"], ["direct", "Direct Transfer", "payout_direct"], ["bulk", "Bulk Upload", "payout_bulk"]].filter((t) => user.services?.[t[2]] !== false);
  const [accounts, setAccounts] = useState([]);
  const blank = { account_id: "", wallet: "aeps", amount: "", mode: "IMPS", mpin: "" };
  const blankDirect = { bank: "", account_no: "", ifsc: "", holder_name: "" };
  const [f, setF] = useState(blank);
  const [type, setType] = useState(tabs[0]?.[0] || "");
  const [direct, setDirect] = useState(blankDirect);
  const [plan, setPlan] = useState(null);
  const [lim, setLim] = useState(null);
  useEffect(() => { api.get("/payout/charges").then(({ data }) => setPlan(data)).catch(() => {}); api.get("/limits").then(({ data }) => setLim(data.payout)).catch(() => {}); }, []);
  const { loading, receipt, setReceipt, run } = useSubmit(() => { setF(blank); setDirect(blankDirect); });
  const setD = (k) => (e) => setDirect({ ...direct, [k]: k === "ifsc" ? e.target.value.toUpperCase() : e.target.value });
  const submit = (e) => {
    e.preventDefault();
    const target = type === "direct" ? { direct, account_id: "" } : { account_id: f.account_id };
    run("/payout", { ...f, ...target, ...geo, amount: Number(f.amount) }, "Payout submitted");
  };
  const [geo, setGeo] = useState({ latitude: "", longitude: "" });
  const reload = () => api.get("/settlement-accounts").then(({ data }) => setAccounts(data));
  useEffect(() => {
    reload();
    navigator.geolocation?.getCurrentPosition((p) => setGeo({ latitude: p.coords.latitude.toFixed(4), longitude: p.coords.longitude.toFixed(4) }), () => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const charge = Number(f.amount) > 1000 ? chargeFor(plan, f.mode, Number(f.amount)) : 0;
  return (
    <div>
      <PageHeader title="Move to Bank (Payout)" subtitle="Send your wallet balance to any bank account — saved account, direct transfer or bulk Excel upload." />
      <div className="mb-4 inline-flex gap-1 rounded-lg bg-slate-100 p-1" data-testid="payout-type-tabs">
        {tabs.map(([v, l]) => (
          <button type="button" key={v} onClick={() => setType(v)} className={`rounded-md px-4 py-2 text-xs font-semibold transition-colors ${type === v ? "bg-white text-blue-800 shadow-sm" : "text-slate-600"}`} data-testid={`payout-type-${v}`}>{l}</button>
        ))}
      </div>
      {!tabs.length && <Card><div className="text-sm text-slate-500" data-testid="payout-no-options">No Move to Bank option is enabled for your account. Contact admin.</div></Card>}
      {type === "bulk" && <BulkPayout />}
      {(type === "saved" || type === "direct") && <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Payout">
          <form onSubmit={submit} className="space-y-4" data-testid="payout-form">
            <Field label="Source Wallet"><Select options={[{ value: "aeps", label: `AEPS Wallet (${inr(wallet.aeps_balance)})` }, { value: "main", label: `Main Wallet (${inr(wallet.balance)})` }]} value={f.wallet} onChange={set("wallet")} data-testid="payout-wallet-select" /></Field>
            {type === "saved" ? (
              <Field label="Bank Account"><Select options={accounts.map((a) => ({ value: a.id, label: `${a.holder_name} · ${a.bank} - ${a.account_no}` }))} placeholder={accounts.length ? "Select Account" : "No saved account — add one or use Direct Transfer"} value={f.account_id} onChange={set("account_id")} required data-testid="payout-account-select" /></Field>
            ) : (
              <div className="grid gap-3 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-2" data-testid="payout-direct-form">
                <div className="text-xs text-slate-500 sm:col-span-2">Account will not be saved. Double-check details before sending.</div>
                <BankInput banks={meta?.banks || []} value={direct.bank} onChange={setD("bank")} testid="payout-direct-bank-select" />
                <input className="np-input" placeholder="Account holder name" value={direct.holder_name} onChange={setD("holder_name")} required data-testid="payout-direct-holder-input" />
                <input className="np-input font-mono" placeholder="Account number" inputMode="numeric" value={direct.account_no} onChange={setD("account_no")} required data-testid="payout-direct-account-input" />
                <input className="np-input font-mono uppercase" placeholder="IFSC" maxLength={11} value={direct.ifsc} onChange={setD("ifsc")} required data-testid="payout-direct-ifsc-input" />
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="payout-amount-input" /></Field>
              <Field label="Mode"><Select options={["IMPS", "NEFT", "RTGS"]} value={f.mode} onChange={set("mode")} data-testid="payout-mode-select" /></Field>
            </div>
            <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
            {lim && <div className="rounded-md bg-blue-50 px-3 py-2 text-xs font-medium text-blue-800" data-testid="payout-limit-hint">Aap ₹{lim.min.toLocaleString("en-IN")} se ₹{lim.max.toLocaleString("en-IN")} tak transfer kar sakte hain (per transaction){lim.daily > 0 ? ` · daily ₹${lim.daily.toLocaleString("en-IN")}` : ""}</div>}
            <div className="text-xs text-slate-500" data-testid="payout-charge-preview">{charge === null ? <span className="text-red-600">No {f.mode} charge set for this amount — contact admin</span> : <>Charge {inr(charge)} · Total debit <b>{inr(Number(f.amount || 0) + charge)}</b></>}</div>
            <Btn type="submit" loading={loading} data-testid="payout-submit-button">Transfer to Bank</Btn>
          </form>
        </Card>
        {type === "saved" && <SettlementAccounts accounts={accounts} reload={reload} banks={meta?.banks || []} />}
      </div>}
      {receipt?.status === "PENDING" && <PayoutStatusBar txn={receipt} onUpdate={setReceipt} />}
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function PayoutStatusBar({ txn, onUpdate }) {
  const { refreshBalance } = useAuth();
  const [loading, setLoading] = useState(false);
  const check = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/payout/${txn.txn_id}/status`);
      onUpdate(data);
      refreshBalance();
      toast.info(`Status: ${data.status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm shadow-lg" data-testid="payout-pending-bar">
      Payout {txn.txn_id} is pending with bank.
      <Btn className="h-8" loading={loading} onClick={check} data-testid="payout-check-status-button">Check Status</Btn>
    </div>
  );
}

export function WalletTransfer() {
  const { wallet } = useAuth();
  const [f, setF] = useState({ amount: "", mpin: "" });
  const { loading, run } = useSubmit(() => setF({ amount: "", mpin: "" }));
  return (
    <div>
      <PageHeader title="AEPS → Main Wallet" subtitle="Move your AEPS earnings into the main wallet to use for recharge, DMT and bills." />
      <Card className="max-w-lg">
        <div className="mb-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-emerald-50 p-3"><div className="np-label">AEPS Wallet</div><div className="font-mono font-semibold">{inr(wallet.aeps_balance)}</div></div>
          <div className="rounded-lg bg-blue-50 p-3"><div className="np-label">Main Wallet</div><div className="font-mono font-semibold">{inr(wallet.balance)}</div></div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); run("/wallet-transfer", { amount: Number(f.amount), mpin: f.mpin }, "Moved to main wallet"); }} className="space-y-4" data-testid="wallet-transfer-form">
          <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="wt-amount-input" /></Field>
          <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
          <Btn type="submit" loading={loading} data-testid="wt-submit-button">Transfer</Btn>
        </form>
      </Card>
    </div>
  );
}

export function PaymentGateway() {
  const [f, setF] = useState({ amount: "", method: "UPI" });
  const { loading, receipt, setReceipt, run } = useSubmit(() => setF({ amount: "", method: "UPI" }));
  return (
    <div>
      <PageHeader title="Payment Gateway — Add Money" subtitle="Load your main wallet instantly using UPI / Card / Net Banking. 1.2% PG charge." />
      <Card className="max-w-lg">
        <form onSubmit={(e) => { e.preventDefault(); run("/pg/pay", { amount: Number(f.amount), method: f.method }, "Wallet loaded"); }} className="space-y-4" data-testid="pg-form">
          <Field label="Amount (100 - 100000)"><input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="pg-amount-input" /></Field>
          <div className="grid grid-cols-3 gap-2">
            {["UPI", "Debit Card", "Net Banking"].map((m) => (
              <button type="button" key={m} onClick={() => setF({ ...f, method: m })} className={`rounded-lg border p-3 text-xs font-semibold ${f.method === m ? "border-blue-700 bg-blue-50 text-blue-800" : "border-slate-200"}`} data-testid={`pg-method-${m.replace(" ", "-").toLowerCase()}`}>{m}</button>
            ))}
          </div>
          <div className="text-xs text-slate-500">You'll receive <b>{inr(Number(f.amount || 0) * 0.988)}</b></div>
          <Btn type="submit" loading={loading} data-testid="pg-pay-button">Proceed to Pay</Btn>
        </form>
      </Card>
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function QrCollect() {
  const { refreshBalance } = useAuth();
  const [f, setF] = useState({ amount: "", payer_name: "" });
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await api.post("/qr/create", { ...f, amount: Number(f.amount) }); setQr(data); } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  return (
    <div>
      <PageHeader title="Collect with QR" subtitle="Generate a dynamic UPI QR for your customer. Amount credited to main wallet on payment." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <form onSubmit={create} className="space-y-4" data-testid="qr-form">
            <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="qr-amount-input" /></Field>
            <Field label="Payer Name (optional)"><input className="np-input" value={f.payer_name} onChange={(e) => setF({ ...f, payer_name: e.target.value })} data-testid="qr-payer-input" /></Field>
            <Btn type="submit" loading={loading} data-testid="qr-generate-button">Generate QR</Btn>
          </form>
        </Card>
        <Card>
          {!qr ? <div className="py-16 text-center text-sm text-slate-400">QR will appear here.</div> : (
            <div className="flex flex-col items-center gap-3" data-testid="qr-display">
              <QRCodeSVG value={qr.details.upi} size={200} />
              <div className="font-mono text-2xl font-bold">{inr(qr.amount)}</div>
              <StatusBadge status={qr.status} />
              <div className="font-mono text-xs text-slate-500">{qr.txn_id}</div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
